import { useSyncExternalStore } from "react";

/**
 * The block, trigger and connector catalogues' Chinese, loaded on demand.
 *
 * These are ~20,000 strings and about 240KB gzipped — a third again the size of
 * everything else this application says. Putting them in `AGENT_COPY` would put
 * them in the main bundle, which means an English reader downloads a quarter of
 * a megabyte of Chinese they will never see, and an Analytics-only reader
 * downloads a catalogue of blocks they never open.
 *
 * So the registry's copy is not a table, it is a subscription: a chunk arrives,
 * calls {@link registerCopy}, and every mounted `t()` re-runs. Until it arrives
 * the strings render in English, which is exactly what an untranslated Agent
 * string already does — so nothing has to wait for it.
 */
let entries: Record<string, string> = {};
let version = 0;
const listeners = new Set<() => void>();

/**
 * Merge a catalogue chunk in.
 *
 * Later chunks lose to earlier ones on a repeated key. The chunks are split by
 * screen, so the same English can appear in two of them, and the one that
 * arrived with the screen the reader is on is the one that already decided how
 * it reads there.
 */
export function registerCopy(chunk: Record<string, string>): void {
	let changed = false;
	for (const [english, chinese] of Object.entries(chunk)) {
		if (english in entries) continue;
		entries[english] = chinese;
		changed = true;
	}
	if (!changed) return;
	version += 1;
	for (const listener of listeners) listener();
}

export function registryCopy(): Record<string, string> {
	return entries;
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/**
 * Read by the provider so a chunk landing re-renders what is already on screen.
 *
 * Without it a block picker that mounted before its catalogue arrived would sit
 * in English until something else happened to re-render it.
 */
export function useRegistryCopyVersion(): number {
	return useSyncExternalStore(
		subscribe,
		() => version,
		// The server renders nothing from this catalogue, and a changing
		// snapshot during hydration would be a mismatch rather than an update.
		() => 0,
	);
}

/** Test seam: the chunks are module-level state and each test needs its own. */
export function resetRegistryCopy(): void {
	entries = {};
	version += 1;
	for (const listener of listeners) listener();
}
